// Tiny audio cues, all synthesised via the Web Audio API so we don't
// need to ship .mp3 assets. Each "kind" plays a distinct musical motif
// so staff and customers can recognise events by ear:
//
//   'self-order'  — door-bell ding-dong (high C → G).  New self-order arrived.
//   'taken'       — single bright affirmative blip (mid-G).  Order confirmed → kitchen.
//   'order-taken' — clear rising "ding-ding-DING" fanfare.  Order placed (big popup).
//   'served'      — three rising bell notes (C → E → G major triad).  An item served.
//   'all-served'  — triumphant five-note ascending run.  Whole order fully served.
//   'paid'        — cash-register style four-note success cadence.  Order paid.
//   'submitted'   — soft two-note "sent" chime (G → C high).  Customer submission ack.
//
// Browsers gate audio behind user interaction. The first call after page
// load may silently no-op; once the user has clicked anywhere, the
// AudioContext is unlocked and subsequent calls play.

let _ctx = null;

function ctx() {
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  return _ctx;
}

function note(c, freq, startAt, duration = 0.36, peak = 0.18, type = 'sine') {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const now = c.currentTime;
  gain.gain.setValueAtTime(0, now + startAt);
  gain.gain.linearRampToValueAtTime(peak, now + startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + startAt + duration);
  osc.connect(gain).connect(c.destination);
  osc.start(now + startAt);
  osc.stop(now + startAt + duration + 0.05);
}

export function playChime(kind) {
  try {
    const c = ctx();
    if (!c) return;
    if (c.state === 'suspended') c.resume().catch(() => {});

    if (kind === 'self-order') {
      // Two-note door-bell ding-dong
      note(c, 1046.5, 0.00, 0.40, 0.18);  // C6
      note(c,  783.99, 0.18, 0.40, 0.18); // G5
    } else if (kind === 'taken') {
      // Single bright "got it" blip
      note(c, 880.0, 0.00, 0.18, 0.18, 'triangle');     // A5
      note(c, 1318.5, 0.06, 0.20, 0.16, 'triangle');    // E6
    } else if (kind === 'order-taken') {
      // Clear, unmistakable "order placed" fanfare — two bright lead-in
      // notes then a held, emphasised top note. Louder + longer than the
      // subtle 'taken' blip so it can't be missed.
      note(c,  783.99, 0.00, 0.20, 0.22, 'triangle');   // G5
      note(c, 1046.50, 0.15, 0.20, 0.22, 'triangle');   // C6
      note(c, 1318.51, 0.32, 0.55, 0.26, 'triangle');   // E6 — held, emphasised
      note(c, 1567.98, 0.32, 0.55, 0.15, 'sine');       // G6 — sparkle layer
    } else if (kind === 'all-served') {
      // Triumphant ascending run with a held, sparkling finish — signals
      // the whole order is fully served.
      note(c,  523.25, 0.00, 0.16, 0.20, 'triangle');   // C5
      note(c,  659.25, 0.12, 0.16, 0.20, 'triangle');   // E5
      note(c,  783.99, 0.24, 0.16, 0.21, 'triangle');   // G5
      note(c, 1046.50, 0.36, 0.16, 0.22, 'triangle');   // C6
      note(c, 1318.51, 0.48, 0.62, 0.26, 'triangle');   // E6 — held finish
      note(c, 1567.98, 0.48, 0.62, 0.14, 'sine');       // G6 — sparkle layer
    } else if (kind === 'served') {
      // C-major triad rising — feels like "everything ready"
      note(c, 523.25, 0.00, 0.22, 0.16, 'sine');        // C5
      note(c, 659.25, 0.10, 0.22, 0.16, 'sine');        // E5
      note(c, 783.99, 0.20, 0.30, 0.18, 'sine');        // G5
      note(c, 1046.5, 0.30, 0.36, 0.18, 'sine');        // C6
    } else if (kind === 'paid') {
      // Cash-register cadence — four notes ascending then a kicker
      note(c, 523.25, 0.00, 0.16, 0.18, 'triangle');    // C5
      note(c, 659.25, 0.08, 0.16, 0.18, 'triangle');    // E5
      note(c, 783.99, 0.16, 0.16, 0.18, 'triangle');    // G5
      note(c, 1046.5, 0.24, 0.42, 0.20, 'triangle');    // C6 (longer)
    } else if (kind === 'staff-call') {
      // Proper "service-bell" ring — three trilling bursts (~2s total) so a
      // customer calling from a table is impossible to miss on the staff
      // display. Each burst rapidly alternates E6/G6 like an insistent
      // telephone/desk bell.
      const ring = (at) => {
        for (let i = 0; i < 7; i++) {
          const f = i % 2 === 0 ? 1318.51 : 1567.98;    // E6 / G6
          note(c, f, at + i * 0.07, 0.10, 0.26, 'triangle');
        }
      };
      ring(0.00);
      ring(0.80);
      ring(1.60);
    } else if (kind === 'submitted') {
      // Quick "sent" — low to high
      note(c, 587.33, 0.00, 0.16, 0.16, 'sine');        // D5
      note(c, 880.0,  0.10, 0.30, 0.18, 'sine');        // A5
    }
  } catch (_) { /* audio unavailable — silent fail */ }
}
